import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Search,
  Plus,
  Loader2,
  ShoppingCart,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-yellow-500/10 text-yellow-500',
  paid: 'bg-green-500/10 text-green-500',
  delivered: 'bg-blue-500/10 text-blue-500',
  cancelled: 'bg-red-500/10 text-red-500',
};

const STATUS_LABELS = {
  pending: 'Oczekuje',
  paid: 'Oplacone',
  delivered: 'Dostarczone',
  cancelled: 'Anulowane',
};

export default function OrdersPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    client_id: '',
    gallery_id: '',
    package_name: '',
    package_price: 0,
    payment_method: 'transfer',
    items: [],
  });
  const [newItem, setNewItem] = useState({ description: '', quantity: 1, unit_price: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, clientsRes, galleriesRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/galleries`),
      ]);
      setOrders(ordersRes.data);
      setClients(clientsRes.data);
      setGalleries(galleriesRes.data);
    } catch (error) {
      toast.error('Nie udalo sie zaladowac danych');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error('Wybierz klienta');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/orders`, form);
      setOrders([response.data, ...orders]);
      setDialogOpen(false);
      setForm({
        client_id: '',
        gallery_id: '',
        package_name: '',
        package_price: 0,
        payment_method: 'transfer',
        items: [],
      });
      toast.success('Zamowienie zostalo utworzone');
    } catch (error) {
      toast.error('Nie udalo sie utworzyc zamowienia');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      toast.success('Status zamowienia zaktualizowany');
    } catch (error) {
      toast.error('Nie udalo sie zmienic statusu');
    }
  };

  const addItem = () => {
    if (!newItem.description.trim()) return;
    setForm({
      ...form,
      items: [...form.items, { ...newItem }],
    });
    setNewItem({ description: '', quantity: 1, unit_price: 0 });
  };

  const removeItem = (index) => {
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index),
    });
  };

  const calculateTotal = () => {
    const itemsTotal = form.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    return (parseFloat(form.package_price) || 0) + itemsTotal;
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch =
      o.order_number?.toLowerCase().includes(search.toLowerCase()) ||
      o.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      o.gallery_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/orders" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Zamowienia</h1>
            <p className="text-muted-foreground mt-1">
              {filteredOrders.length} zamowien
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nowe zamowienie
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Szukaj zamowien..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] h-12">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie</SelectItem>
              <SelectItem value="pending">Oczekuje</SelectItem>
              <SelectItem value="paid">Oplacone</SelectItem>
              <SelectItem value="delivered">Dostarczone</SelectItem>
              <SelectItem value="cancelled">Anulowane</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders Table */}
        {filteredOrders.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="bg-card border-border">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-6">Nr zamowienia</TableHead>
                      <TableHead>Klient</TableHead>
                      <TableHead>Galeria</TableHead>
                      <TableHead className="text-right">Kwota</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order, idx) => (
                      <motion.tr
                        key={order.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: idx * 0.03 }}
                        className="border-b transition-colors hover:bg-muted/50"
                      >
                        <TableCell className="pl-6 py-4 font-mono text-sm">
                          {order.order_number}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{order.client_name || '-'}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-foreground">{order.gallery_name || '-'}</span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {order.total_amount?.toFixed(2)} {order.currency || 'PLN'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Select
                            value={order.status}
                            onValueChange={(value) => handleStatusChange(order.id, value)}
                          >
                            <SelectTrigger className={`w-32 h-8 text-xs ${STATUS_COLORS[order.status]}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Oczekuje</SelectItem>
                              <SelectItem value="paid">Oplacone</SelectItem>
                              <SelectItem value="delivered">Dostarczone</SelectItem>
                              <SelectItem value="cancelled">Anulowane</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {order.created_at ? new Date(order.created_at).toLocaleDateString('pl-PL') : '-'}
                        </TableCell>
                      </motion.tr>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">
                {search || statusFilter !== 'all'
                  ? 'Nie znaleziono zamowien'
                  : 'Brak zamowien'
                }
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {search || statusFilter !== 'all'
                  ? 'Sprobuj zmienic kryteria wyszukiwania'
                  : 'Utwórz pierwsze zamowienie, aby rozpoczac'
                }
              </p>
              {!search && statusFilter === 'all' && (
                <Button onClick={() => setDialogOpen(true)} className="mt-2">
                  <Plus className="w-4 h-4 mr-2" />
                  Utwórz zamowienie
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* New Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nowe zamowienie</DialogTitle>
            <DialogDescription>
              Utwórz nowe zamowienie dla klienta
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Klient *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz klienta" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Galeria (opcjonalnie)</Label>
              <Select value={form.gallery_id} onValueChange={(v) => setForm({ ...form, gallery_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz galerie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Brak</SelectItem>
                  {galleries.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nazwa pakietu</Label>
                <Input
                  placeholder="np. Pakiet Standard"
                  value={form.package_name}
                  onChange={(e) => setForm({ ...form, package_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Cena pakietu (PLN)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.package_price}
                  onChange={(e) => setForm({ ...form, package_price: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Metoda platnosci</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfer">Przelew</SelectItem>
                  <SelectItem value="card">Karta</SelectItem>
                  <SelectItem value="cash">Gotowka</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Items */}
            <div className="space-y-2">
              <Label>Pozycje zamowienia</Label>
              {form.items.length > 0 && (
                <div className="space-y-2 mb-2">
                  {form.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm bg-muted/50 p-2 rounded">
                      <span className="flex-1">{item.description}</span>
                      <span>{item.quantity} x {item.unit_price.toFixed(2)}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="Opis"
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  className="flex-1"
                />
                <Input
                  type="number"
                  min="1"
                  placeholder="Ilosc"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                  className="w-20"
                />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Cena"
                  value={newItem.unit_price}
                  onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                  className="w-24"
                />
                <Button type="button" variant="outline" onClick={addItem}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <span className="font-medium">Suma</span>
              <span className="text-xl font-bold">{calculateTotal().toFixed(2)} PLN</span>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Anuluj
              </Button>
              <Button type="submit" disabled={creating}>
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Utwórz zamowienie
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
